import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { BellRing, CheckCircle2, AlertTriangle } from "lucide-react";

export default function Compliance() {
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get("/followups/compliance");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const sendReminders = async () => {
    const { data } = await api.post("/followups/send-reminders");
    toast.success(`Reminders sent to ${data.reminders_sent} employee(s)`);
  };

  if (!data) return <div className="h-40" />;
  const rows = [...data.rows].sort((a, b) => a.compliance - b.compliance);
  const overall = rows.length ? Math.round(rows.reduce((s, r) => s + r.compliance, 0) / rows.length) : 100;

  return (
    <>
      <PageHeader
        title="Follow-up Compliance"
        subtitle={`Company rule: every active customer needs one follow-up per week. Week of ${data.week_start}.`}
        testId="compliance-page"
        actions={
          <Button onClick={sendReminders} className="rounded-sm gap-2 font-bold uppercase text-xs tracking-wide" data-testid="send-reminders-button">
            <BellRing className="h-4 w-4" /> Send Reminders
          </Button>
        }
      />

      <div className="border border-border bg-card rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Overall Compliance</p>
            <p className="text-5xl font-display font-black tracking-tighter mt-1">{overall}<span className="text-2xl">%</span></p>
          </div>
          <div className={`h-16 w-16 rounded-sm flex items-center justify-center ${overall >= 80 ? "bg-success/15 text-success" : "bg-[hsl(var(--warning))]/15 text-[hsl(45_100%_32%)]"}`}>
            {overall >= 80 ? <CheckCircle2 className="h-8 w-8" /> : <AlertTriangle className="h-8 w-8" />}
          </div>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-wide">Employee</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide hidden sm:table-cell">Department</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide text-center">Active</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide text-center">Done</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide text-center">Pending</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Compliance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.employee_id} className="hover:bg-secondary/40" data-testid={`compliance-row-${r.employee_id}`}>
                  <TableCell className="font-semibold">{r.employee_name}</TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{r.department}</TableCell>
                  <TableCell className="text-center">{r.total_clients}</TableCell>
                  <TableCell className="text-center text-success font-bold">{r.followups_done}</TableCell>
                  <TableCell className="text-center font-bold" style={{ color: r.pending > 0 ? "hsl(var(--destructive))" : "inherit" }}>{r.pending}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 bg-secondary rounded-sm overflow-hidden">
                        <div className="h-full" style={{ width: `${r.compliance}%`, background: r.compliance >= 80 ? "hsl(var(--success))" : r.compliance >= 40 ? "hsl(var(--warning))" : "hsl(var(--destructive))" }} />
                      </div>
                      <span className="text-sm font-bold w-12">{r.compliance}%</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
